import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { FaApple } from 'react-icons/fa';
import Seo from '../components/Seo';
import api from '../utils/api';

const IOS_URL = 'https://apps.apple.com/app/moneyfeed-ai/id6788915539';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.moneyfeed.app';

export const detectPlatform = (ua = navigator.userAgent, touch = navigator.maxTouchPoints) => {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touch > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const storeUrl = (platform, src) => (platform === 'ios'
  ? IOS_URL
  : `${ANDROID_URL}&referrer=${encodeURIComponent(`utm_source=moneyfeed&utm_medium=link&utm_campaign=${src}`)}`);

const PlayLogo = ({ className }) => (
  <svg viewBox="0 0 512 512" className={className} aria-hidden="true">
    <path fill="#00A0FF" d="M40 20 L40 492 L296 256 Z" />
    <path fill="#00D67C" d="M40 20 L380 206 L296 256 Z" />
    <path fill="#FF3A44" d="M40 492 L296 256 L380 306 Z" />
    <path fill="#FFC400" d="M380 206 L472 256 L380 306 L296 256 Z" />
  </svg>
);

const Badge = ({ href, icon: Icon, small, big, testId }) => (
  <a href={href} className="inline-flex items-center justify-start gap-3 w-[200px] h-[60px] bg-black text-white rounded-xl px-5 hover:bg-gray-800 transition-colors" data-testid={testId}>
    <Icon className="h-7 w-7 flex-shrink-0" />
    <span className="text-left leading-tight">
      <span className="block text-[10px] opacity-80">{small}</span>
      <span className="block text-lg font-semibold -mt-0.5">{big}</span>
    </span>
  </a>
);

export default function AppLink() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const src = (params.get('src') || 'direct').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40) || 'direct';
  const [platform] = useState(() => detectPlatform());

  useEffect(() => {
    if (Capacitor.isNativePlatform()) { navigate('/dashboard', { replace: true }); return; }
    const key = `mf_app_click_${src}`;
    if (Date.now() - Number(sessionStorage.getItem(key) || 0) > 10000) {
      sessionStorage.setItem(key, String(Date.now()));
      api.post('/app-link/click', { src, platform }).catch(() => {});
    }
    if (platform !== 'desktop') {
      const t = setTimeout(() => window.location.replace(storeUrl(platform, src)), 250);
      return () => clearTimeout(t);
    }
  }, [navigate, platform, src]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0FDF4] px-6" data-testid="app-link-page">
      <Seo title="Get the MoneyFeed app" description="Download MoneyFeed on the App Store or Google Play." path="/app" noindex />
      <div className="text-center max-w-md">
        <img src="/logo192.png" alt="MoneyFeed" className="h-16 w-16 mx-auto mb-5 rounded-2xl" />
        <h1 className="text-3xl font-bold text-[#064E3B] mb-2">Get the MoneyFeed app</h1>
        <p className="text-gray-600 mb-8" data-testid="app-link-message">
          {platform === 'desktop'
            ? 'Scan receipts, see every line item and export to Excel from your phone.'
            : 'Opening the store…'}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {platform !== 'android' && <Badge href={storeUrl('ios', src)} icon={FaApple} small="Download on the" big="App Store" testId="app-link-ios" />}
          {platform !== 'ios' && <Badge href={storeUrl('android', src)} icon={PlayLogo} small="GET IT ON" big="Google Play" testId="app-link-android" />}
        </div>
      </div>
    </div>
  );
}
